<!--
    @component
    Helper component for using SVGs as "invertible" masks, for use with "punch-out"
    styles where the SVG glyph shows the background through a rectangular shape.

    @prop maskSvg {SvelteComponent} - SVG to be used as mask, imported as a Svelte component.
    @prop rectWidth {number} - Width of rectangle, in pixels.
    @prop rectHeight {number} - Height of rectangle, in pixels.
    @prop maskWidth {number} - Width of mask SVG, in pixels.
    @prop maskHeight {number} - Height of mask SVG, in pixels.
    @prop maskX {number} - Left offset of mask, in pixels.
    @prop maskY {number} - Top offset of mask, in pixels
    @prop inverted {boolean} - Whether the mask is inverted to use "punch-out" style or not.

    @cssprop --svgFill {color} - Fill color for the SVG.
-->
<script lang="ts">
    import type { ComponentType } from 'svelte';
    import { getUniqueIdGenerator } from '@amp/web-app-components/src/utils/uniqueId';

    // Generate a random ID for the mask
    const maskId = getUniqueIdGenerator()();

    export let maskSvg: ComponentType;

    // Width/height of rectangle, in pixels
    export let rectWidth: number = 28;
    export let rectHeight: number = 28;

    // Width/height of mask, in pixels
    export let maskWidth: number = 18;
    export let maskHeight: number = 18;

    // Default to centering the mask inside the rect
    export let maskX: number = (rectWidth - maskWidth) / 2;
    export let maskY: number = (rectHeight - maskHeight) / 2;

    // Controls how the mask will be applied
    // When `true`, use the "punch out" style.
    // When `false`, the mask SVG is displayed with its fill.
    export let inverted: boolean = true;
</script>

<svg
    data-testid="invertible-mask-svg"
    class="invertible-mask {inverted
        ? 'invertible-mask--inverted'
        : 'invertible-mask--not-inverted'}"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 {rectWidth} {rectHeight}"
    width={rectWidth}
    height={rectHeight}
    role="presentation"
>
    <mask data-testid="invertible-mask" id={maskId}>
        <!-- Everything under a white pixel will be visible -->
        <rect width="100%" height="100%" fill={inverted ? 'white' : 'black'} />

        <!-- Everything under a black pixel will be invisible -->
        <svelte:component
            this={maskSvg}
            width={maskWidth}
            height={maskHeight}
            x={maskX}
            y={maskY}
            fill={inverted ? 'black' : 'white'}
        />
    </mask>

    <!-- with this mask applied, we "punch" the icon shape into the rectangle -->
    <rect
        data-testid="invertible-mask-rect"
        width="100%"
        height="100%"
        mask="url(#{maskId})"
    />
</svg>

<style lang="scss">
    .invertible-mask {
        border-radius: inherit;
        fill: var(--svgFill, var(--keyColor));
    }
</style>
